// src/accounts/pages/ProjectsPage.jsx
//
// Projects admin list page. Route: /admin/projects.
// Scoped to whichever Account is currently selected in the topbar — reads
// `engagements`/`currentAccount` straight off the shared WorkspaceContext,
// which already scopes the engagements fetch server-side per account, so
// switching Accounts in the topbar refilters this page automatically.
//
// Same visual pass as AccountsPage: a KPI summary row and initials-chip
// rows instead of a bare table, so the two admin pages read as one
// cohesive feature.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FolderGit2, Plus, Users, Activity, ShieldCheck, Eye } from "lucide-react";
import { PageHeader, Button, EmptyState, DataTable, StatusBadge, SkeletonCardGrid, SkeletonTable } from "../../shared/ui";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAuth } from "../../context/AuthContext";
import RegisterProjectModal from "../components/RegisterProjectModal";
import StatTile from "../components/StatTile";

function initialsFor(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function ProjectsPage() {
  const { currentAccount, engagements, loading, error, createEngagement } = useWorkspace();
  const { user, hasPermission, hasGlobalPermission } = useAuth();
  const [registerOpen, setRegisterOpen] = useState(false);

  // Mirrors the removed standalone Projects page's own gating: an org-wide
  // holder can create regardless of which Account is selected; an
  // account-scoped holder only if their grant covers this specific Account.
  const canCreate = Boolean(
    currentAccount &&
    (hasGlobalPermission("conductor:engagement:create") ||
      hasPermission("conductor:engagement:create", { accountId: currentAccount.account_id }))
  );

  const totalTeamSize = engagements.reduce((sum, e) => sum + (e.team_members?.length || 0), 0);
  const activeCount = engagements.filter((e) => e.current_stage !== "closed").length;

  async function handleRegister(payload) {
    const created = await createEngagement({
      client_name: payload.name,
      platform: payload.platform,
      contract_type: payload.contractType,
      opportunity_id: payload.opportunityId,
    });
    return { ...created, name: created.client_name };
  }

  // account_manager_id records who created this project (set once at
  // creation, see create_engagement_handler.py) — it's not tied to who
  // currently holds account_admin on the Account, which can be several
  // people and can change over time. Labelled "Created by" rather than
  // "Account manager" to be honest about that. It's only ever a bare user
  // id — the engagement DTO has no name field alongside it — so the name
  // comes from team_members (already resolved with username/email by
  // list_engagements_handler.py; the creator is auto-added to the team),
  // falling back to the raw id only if they were later removed from it.
  function createdByLabel(engagement) {
    const { account_manager_id: createdById, team_members: teamMembers } = engagement;
    const creator = teamMembers?.find((m) => Number(m.user_id) === Number(createdById));
    return creator?.username || creator?.email || String(createdById);
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "client_name",
        header: "Project name",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-full font-bold shrink-0"
              style={{ width: 30, height: 30, fontSize: 11, background: "var(--jade-gold-400)", color: "var(--jade-blue-900)" }}
            >
              {initialsFor(row.original.client_name)}
            </div>
            <span className="font-medium text-ink">{row.original.client_name}</span>
          </div>
        ),
      },
      {
        accessorKey: "account_manager_id",
        header: "Created by",
        cell: ({ row }) => <span className="text-muted">{createdByLabel(row.original)}</span>,
      },
      {
        accessorKey: "current_stage",
        header: "Stage",
        width: 140,
        cell: ({ getValue }) => <StatusBadge map="engagementStage" status={getValue()} size="sm" />,
      },
      {
        accessorKey: "platform",
        header: "Platform",
        width: 110,
        cell: ({ getValue }) => <span className="text-muted">{getValue() || "orion"}</span>,
      },
      {
        id: "teamSize",
        header: "Team size",
        align: "center",
        width: 110,
        cell: ({ row }) => (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-xs font-semibold">
            {row.original.team_members?.length || 0}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        width: 100,
        sortAllowed: false,
        cell: ({ row }) => {
          const engagement = row.original;
          // Same gate EngagementDetailPage.jsx itself uses for its team
          // section (canManageTeam) — no point linking to a Manage page
          // that will just render its team-editing form as hidden anyway.
          const canManage =
            hasGlobalPermission("conductor:engagement:manage") ||
            hasPermission("conductor:engagement:manage", {
              accountId: engagement.account_id,
              projectId: engagement.engagement_id,
            });
          // EngagementDetailPage itself is reachable by a project-scoped
          // viewer too (see its own top-of-file comment) -- it just omits
          // the team-management Actions column for them. So this link
          // must stay visible either way; only the label/icon changes.
          return (
            <Link
              to={`/admin/projects/${engagement.engagement_id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline"
            >
              {canManage ? (
                <>
                  <ShieldCheck size={13} /> Manage
                </>
              ) : (
                <>
                  <Eye size={13} /> View
                </>
              )}
            </Link>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, hasPermission, hasGlobalPermission]
  );

  return (
    <div>
      <PageHeader
        title="Projects"
        subtitle={
          currentAccount
            ? <>Projects registered under <span className="font-medium text-ink">{currentAccount.name}</span>. Switch Accounts in the topbar to see another Account's Projects.</>
            : "Select an Account in the topbar to see its Projects."
        }
        actions={
          canCreate && (
            <Button variant="primary" onClick={() => setRegisterOpen(true)}>
              <Plus size={15} /> Register Project
            </Button>
          )
        }
      />

      {currentAccount && !error && (loading || engagements.length > 0) && (
        loading ? (
          <SkeletonCardGrid cards={3} className="mb-5" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <StatTile icon={FolderGit2} label="Total Projects" value={engagements.length} hint={currentAccount.name} />
            <StatTile icon={Activity} label="Active Projects" value={activeCount} />
            <StatTile icon={Users} label="People Across Projects" value={totalTeamSize} />
          </div>
        )
      )}

      {!currentAccount ? (
        <EmptyState icon={FolderGit2} title="No Account selected" description="Choose an Account from the topbar switcher first." />
      ) : loading ? (
        <SkeletonTable rows={5} columns={6} className="rounded-card border border-line bg-white" />
      ) : error ? (
        <EmptyState icon={FolderGit2} title="Couldn't load Projects" description={error} />
      ) : engagements.length === 0 ? (
        <EmptyState
          icon={FolderGit2}
          title="No Projects yet"
          description={`Register the first Project under ${currentAccount.name}.`}
          action={
            canCreate && (
              <Button variant="secondary" onClick={() => setRegisterOpen(true)}>
                <Plus size={15} /> Register Project
              </Button>
            )
          }
        />
      ) : (
        <DataTable table={{ columns, rows: engagements }} canSearch />
      )}

      <RegisterProjectModal
        open={registerOpen}
        accountName={currentAccount?.name}
        onClose={() => setRegisterOpen(false)}
        onRegister={handleRegister}
      />
    </div>
  );
}
